import React, { useEffect, useState } from 'react';
import { X, Loader2, Tag } from 'lucide-react';
import { brandsApi } from '../api';

/**
 * Bulk-assign-brand modal used by Item Master + EBO Master.
 * Props:
 *   open       — bool
 *   onClose    — () => void
 *   onSubmit   — async (brand: string) => void  (parent handles the actual API call + toast + refresh)
 *   count      — number of rows selected
 *   entityLabel — 'SKU' | 'EBO branch'  (used in copy)
 *   primary    — theme primary color
 */
export default function BulkAssignBrandModal({ open, onClose, onSubmit, count, entityLabel, primary = '#9C3D54' }) {
  const [brands, setBrands] = useState([]);
  const [brand, setBrand] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBrand('');
    brandsApi.list({ only_active: true }).then(setBrands).catch(() => setBrands([]));
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(brand);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bulk-assign-brand-modal"
    >
      <div className="fm-card p-5 w-full max-w-[440px]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 font-semibold text-[16px]">
            <Tag size={16} style={{ color: primary }} />
            Assign brand to {count} {count === 1 ? entityLabel : (entityLabel === 'EBO branch' ? 'EBO branches' : `${entityLabel}s`)}
          </div>
          <button onClick={onClose} data-testid="bulk-assign-brand-close-btn"><X size={16} /></button>
        </div>

        <p className="text-[12.5px] text-[#7a6f5c] mb-3 leading-relaxed">
          Rewrites the <span className="font-mono text-[11.5px]">brand</span> field on every selected row.
          Choose <span className="italic">No brand scope</span> to make them visible across all brands.
        </p>

        <label className="text-[12.5px] font-medium">Brand</label>
        <select
          data-testid="bulk-assign-brand-select"
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          className="fm-select fm-focus mt-1 w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white"
        >
          <option value="">— No brand scope —</option>
          {brands.map((b) => (
            <option key={b.name} value={b.name}>{b.name}</option>
          ))}
        </select>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-2 border border-[#E7E2D3] rounded-md text-[13.5px]">Cancel</button>
          <button
            data-testid="bulk-assign-brand-submit-btn"
            onClick={submit}
            disabled={submitting}
            className="px-4 py-2 rounded-md text-white text-[13.5px] font-medium disabled:opacity-60 inline-flex items-center gap-2"
            style={{ background: primary }}
          >
            {submitting && <Loader2 size={13} className="animate-spin" />}
            Assign to {count} row{count === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
}
